import { supabase } from '../lib/supabase';

const BOOTH_IMAGES_BUCKET = 'booth-images';

export const storageService = {
  /**
   * Upload booth image (logo or banner) to storage
   * @param {File} file - Image file to upload
   * @param {string} instituteId - Institute ID for folder organization
   * @param {string} imageType - 'logo' or 'banner'
   * @returns {Promise<{filePath: string, publicUrl: string}>}
   */
  async uploadBoothImage(file, instituteId, imageType) {
    try {
      // Generate unique filename with timestamp
      const timestamp = Date.now();
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${imageType}-${timestamp}.${fileExt}`;
      const filePath = `${instituteId}/${fileName}`;

      // Upload file to storage
      const { data, error } = await supabase?.storage
        ?.from(BOOTH_IMAGES_BUCKET)
        ?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Get public URL
      const { data: urlData } = supabase?.storage
        ?.from(BOOTH_IMAGES_BUCKET)
        ?.getPublicUrl(filePath);

      return {
        filePath: data?.path,
        publicUrl: urlData?.publicUrl
      };
    } catch (error) {
      console.error('Error uploading booth image:', error);
      throw new Error(error?.message || 'Failed to upload image');
    }
  },

  /**
   * Delete booth image from storage
   * @param {string} filePath - Path to file in storage
   * @returns {Promise<void>}
   */
  async deleteBoothImage(filePath) {
    try {
      const { error } = await supabase?.storage
        ?.from(BOOTH_IMAGES_BUCKET)
        ?.remove([filePath]);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting booth image:', error);
      throw new Error(error?.message || 'Failed to delete image');
    }
  },

  /**
   * Get public URL for booth image
   * @param {string} filePath - Path to file in storage
   * @returns {string} Public URL
   */
  getBoothImageUrl(filePath) {
    if (!filePath) return null;
    
    const { data } = supabase?.storage
      ?.from(BOOTH_IMAGES_BUCKET)
      ?.getPublicUrl(filePath);

    return data?.publicUrl;
  },

  /**
   * Update institute profile with new image URLs
   * @param {string} instituteId - Institute profile ID
   * @param {Object} imageUrls - Object with logoUrl and/or bannerUrl
   * @returns {Promise<Object>}
   */
  async updateInstituteImages(instituteId, imageUrls) {
    try {
      const updateData = {};
      
      if (imageUrls?.logoUrl) {
        updateData.logo_url = imageUrls?.logoUrl;
      }
      
      if (imageUrls?.bannerUrl) {
        updateData.banner_url = imageUrls?.bannerUrl;
      }

      const { data, error } = await supabase
        ?.from('institute_profiles')
        ?.update(updateData)
        ?.eq('id', instituteId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        logoUrl: data?.logo_url,
        bannerUrl: data?.banner_url,
        instituteName: data?.institute_name
      };
    } catch (error) {
      console.error('Error updating institute images:', error);
      throw new Error(error?.message || 'Failed to update institute images');
    }
  }
};